import { apiClient } from './api.client';
import type {
  CasesResponse,
  CaseDetail,
  CaseDetailResponse,
  CaseFilters,
  CreateCaseDto,
  UpdateCaseDto,
  CaseDetailPageResponse,
} from '@lnr-v2/types';

/**
 * Cases API Service
 * Provides methods to interact with the cases API endpoints
 */
class CasesApiService {
  private readonly endpoint = '/cases';

  /**
   * Get paginated list of cases
   * @param filters - Optional filters for cases
   * @returns Promise with cases response
   */
  async getCases(filters?: CaseFilters): Promise<CasesResponse> {
    const response = await apiClient.get<CasesResponse>(this.endpoint, {
      params: filters,
    });
    return response.data;
  }

  /**
   * Get single case by slug
   * @param slug - Case slug
   * @returns Promise with case detail
   */
  async getCaseBySlug(slug: string): Promise<CaseDetailPageResponse> {
    const response = await apiClient.get<CaseDetailPageResponse>(
      `${this.endpoint}/${slug}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error?.message || 'Failed to fetch case details'
      );
    }
    return response.data;
  }

  /**
   * Create new case (requires authentication)
   * @param data - Case creation data
   * @returns Promise with created case
   */
  async createCase(data: CreateCaseDto): Promise<CaseDetail> {
    const response = await apiClient.post<CaseDetailResponse>(
      this.endpoint,
      data
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to create case');
    }
    return response.data.data;
  }

  /**
   * Update existing case (requires authentication)
   * @param id - Case ID
   * @param data - Case update data
   * @returns Promise with updated case
   */
  async updateCase(id: string, data: UpdateCaseDto): Promise<CaseDetail> {
    const response = await apiClient.put<CaseDetailResponse>(
      `${this.endpoint}/${id}`,
      data
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to update case');
    }
    return response.data.data;
  }

  /**
   * Get single case by preview token (public - any status)
   */
  async getCaseByPreviewToken(token: string): Promise<CaseDetailPageResponse> {
    const response = await apiClient.get<CaseDetailPageResponse>(
      `${this.endpoint}/preview/${token}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error?.message || 'Failed to fetch case preview'
      );
    }
    return response.data;
  }

  /**
   * Delete case (soft delete by archiving, requires authentication)
   * @param id - Case ID
   * @returns Promise<void>
   */
  async deleteCase(id: string): Promise<void> {
    await apiClient.delete(`${this.endpoint}/${id}`);
  }
}

// Export singleton instance
export const casesApiService = new CasesApiService();
